/* "Who are we asking?" — chosen when a question set is sent.

   Send time is the moment this is known, and the only moment it is cheap to
   capture: afterwards the answers are already in the Vault and somebody has to
   remember who gave them.

   Deliberately NOT the same choice as the byline of whatever article prompted
   the questions. A blog can be signed by one person and quote another who
   answered, and the words belong to whoever said them. */
import { useQuery } from '@tanstack/react-query';
import { Select } from '@/components/ui';
import { getExperts } from '@/data/client';

export function ExpertPicker({ value, onChange, label = 'Answers from', disabled, approvedOnly }: {
  value: string;
  onChange: (id: string) => void;
  label?: string;
  disabled?: boolean;
  /* For a byline, which links to a published author page. Attribution has no
     such gate: who said something is a fact whether or not their bio has been
     proofread. */
  approvedOnly?: boolean;
}) {
  const { data } = useQuery({ queryKey: ['experts'], queryFn: getExperts });
  const all = data?.experts ?? [];
  const people = approvedOnly ? all.filter((p) => p.state === 'approved') : all;
  const held = approvedOnly ? all.length - people.length : 0;
  /* Nothing to choose from is not worth a disabled dropdown: it would read as
     broken. The set still sends, unattributed, which is what happened before
     any of this existed. */
  /* Say why the list is short rather than silently offering fewer names. A
     control that quietly drops an option is how the FAQ switches went unnoticed
     for weeks. */
  if (!people.length) {
    return held > 0
      ? (
        <span className="dash-muted" style={{ fontSize: 12 }}>
          {held} {held === 1 ? 'profile is' : 'profiles are'} awaiting approval — a page can't be signed until one is approved.
        </span>
      )
      : null;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span className="dash-muted" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{label}</span>
      <Select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        options={[
          { value: '', label: 'Not recorded' },
          ...people.map((p) => ({ value: p.id, label: p.title ? `${p.name} — ${p.title}` : p.name })),
        ]}
      />
      {held > 0 && (
        <span className="dash-muted" style={{ fontSize: 11 }}>
          {held} more awaiting approval
        </span>
      )}
    </div>
  );
}
